import { useCallback, useEffect, useRef, useState } from "react";
import { WHATSAPP_WEB_OFFICIAL_URL } from "@/modules/despacho/clientesAtencionViewMode";

const POPUP_NAME = "visor-fiscal-whatsapp-web";

function screenCoordsForElement(el) {
  if (!el || typeof window === "undefined") return null;
  const rect = el.getBoundingClientRect();
  return {
    left: Math.round(window.screenX + rect.left),
    top: Math.round(window.screenY + rect.top),
    width: Math.max(320, Math.round(rect.width)),
    height: Math.max(360, Math.round(rect.height)),
  };
}

function buildPopupFeatures({ left, top, width, height }) {
  return [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    "menubar=no",
    "toolbar=no",
    "location=no",
    "status=no",
    "resizable=yes",
    "scrollbars=yes",
  ].join(",");
}

/**
 * Ventana popup anclada al recuadro del DOM (WhatsApp Web oficial).
 * Meta no permite iframe; esto simula integración visual sobre el hueco reservado.
 */
export function useWhatsAppAnchoredWindow({ enabled = true } = {}) {
  const anchorRef = useRef(null);
  const popupRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [syncBlocked, setSyncBlocked] = useState(false);

  const syncPosition = useCallback(() => {
    const popup = popupRef.current;
    const anchor = anchorRef.current;
    if (!popup || popup.closed || !anchor) return;
    const coords = screenCoordsForElement(anchor);
    if (!coords) return;
    try {
      popup.moveTo(coords.left, coords.top);
      popup.resizeTo(coords.width, coords.height);
      setSyncBlocked(false);
    } catch {
      setSyncBlocked(true);
    }
  }, []);

  const closePopup = useCallback(() => {
    const popup = popupRef.current;
    if (popup && !popup.closed) {
      try {
        popup.close();
      } catch {
        // ignore
      }
    }
    popupRef.current = null;
    setIsOpen(false);
  }, []);

  const openAnchored = useCallback(() => {
    if (typeof window === "undefined" || !enabled) return null;
    const anchor = anchorRef.current;
    if (!anchor) return null;

    const existing = popupRef.current;
    if (existing && !existing.closed) {
      syncPosition();
      existing.focus();
      setIsOpen(true);
      return existing;
    }

    const coords = screenCoordsForElement(anchor);
    if (!coords) return null;

    const popup = window.open(
      WHATSAPP_WEB_OFFICIAL_URL,
      POPUP_NAME,
      buildPopupFeatures(coords),
    );
    if (!popup) {
      setIsOpen(false);
      return null;
    }
    popupRef.current = popup;
    setIsOpen(true);
    syncPosition();
    popup.focus();
    return popup;
  }, [enabled, syncPosition]);

  const openInNewTab = useCallback(() => {
    if (typeof window === "undefined") return;
    window.open(WHATSAPP_WEB_OFFICIAL_URL, "_blank", "noopener,noreferrer");
  }, []);

  useEffect(() => {
    if (!enabled || !isOpen) return undefined;

    const onResize = () => syncPosition();
    const onScroll = () => syncPosition();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    const interval = window.setInterval(() => {
      const popup = popupRef.current;
      if (!popup || popup.closed) {
        popupRef.current = null;
        setIsOpen(false);
        return;
      }
      syncPosition();
    }, 400);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      window.clearInterval(interval);
    };
  }, [enabled, isOpen, syncPosition]);

  useEffect(() => () => closePopup(), [closePopup]);

  return {
    anchorRef,
    isOpen,
    syncBlocked,
    openAnchored,
    closePopup,
    openInNewTab,
    syncPosition,
  };
}
